import React, { useState } from 'react';
import { Search, Package, Truck, Save, RotateCcw, CheckCircle2, AlertCircle, FileText, ChevronDown, ChevronRight, Plus, Trash2, DollarSign, Briefcase } from 'lucide-react';
// Mock Data
interface Charge {
  id: string;
  fiscalYear: string;
  rateName: string;
  rateType: string;
  amount: number;
}
interface Service {
  id: string;
  name: string;
  category: string;
  charges: Charge[];
}
const REGISTERED_SERVICES: Service[] = [{
  id: 'srv-1',
  name: 'Container Scanning',
  category: 'Import',
  charges: [{
    id: 'chg-1-1',
    fiscalYear: '2082/83',
    rateName: 'Standard Rate',
    rateType: 'Fixed',
    amount: 2500
  }, {
    id: 'chg-1-2',
    fiscalYear: '2082/83',
    rateName: 'Express Rate',
    rateType: 'Fixed',
    amount: 4000
  }]
}, {
  id: 'srv-2',
  name: 'Weighbridge Service',
  category: 'General',
  charges: [{
    id: 'chg-2-1',
    fiscalYear: '2082/83',
    rateName: 'Single Weighment',
    rateType: 'Per Unit',
    amount: 150
  }, {
    id: 'chg-2-2',
    fiscalYear: '2082/83',
    rateName: 'Double Weighment',
    rateType: 'Per Unit',
    amount: 250
  }]
}, {
  id: 'srv-3',
  name: 'Cargo Handling',
  category: 'Handling',
  charges: [{
    id: 'chg-3-1',
    fiscalYear: '2082/83',
    rateName: 'Per Ton Rate',
    rateType: 'Per Ton',
    amount: 350
  }, {
    id: 'chg-3-2',
    fiscalYear: '2082/83',
    rateName: 'Per CBM Rate',
    rateType: 'Per CBM',
    amount: 450
  }]
}, {
  id: 'srv-4',
  name: 'Terminal Storage',
  category: 'Storage',
  charges: [{
    id: 'chg-4-1',
    fiscalYear: '2082/83',
    rateName: 'Covered',
    rateType: 'Per Day',
    amount: 10
  }, {
    id: 'chg-4-2',
    fiscalYear: '2082/83',
    rateName: 'Open Yard',
    rateType: 'Per Day',
    amount: 5
  }]
}, {
  id: 'srv-5',
  name: 'Documentation Fee',
  category: 'Export',
  charges: [{
    id: 'chg-5-1',
    fiscalYear: '2082/83',
    rateName: 'Standard',
    rateType: 'Fixed',
    amount: 1000
  }, {
    id: 'chg-5-2',
    fiscalYear: '2082/83',
    rateName: 'Expedited',
    rateType: 'Fixed',
    amount: 2000
  }]
}];
interface GatepassData {
  gatepassNumber: string;
  vehicleNo: string;
  commodity: string;
  vehicleType: string;
  status: string;
  partyName: string;
}
interface AppliedCharge extends Charge {
  appliedId: string;
  serviceName: string;
  serviceId: string;
}
export function ApplyService() {
  const [searchQuery, setSearchQuery] = useState('');
  const [gatepassData, setGatepassData] = useState<GatepassData | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success'>('idle');
  const [expandedServiceId, setExpandedServiceId] = useState<string | null>(null);
  const [appliedCharges, setAppliedCharges] = useState<AppliedCharge[]>([]);
  const handleSearch = () => {
    setErrors({});
    if (!searchQuery.trim()) {
      setErrors({
        search: 'Please enter a gatepass number'
      });
      return;
    }
    setIsSearching(true);
    setTimeout(() => {
      setGatepassData({
        gatepassNumber: searchQuery.toUpperCase(),
        vehicleNo: 'WB73G9558',
        commodity: 'ELECTRONICS',
        vehicleType: 'CONTAINER TRUCK',
        status: 'Inside Terminal',
        partyName: 'ABC Logistics Pvt Ltd'
      });
      setIsSearching(false);
      setExpandedServiceId(null);
      setAppliedCharges([]);
    }, 600);
  };
  const toggleServiceExpand = (serviceId: string) => {
    setExpandedServiceId(expandedServiceId === serviceId ? null : serviceId);
  };
  const handleApplyCharge = (service: Service, charge: Charge) => {
    // Check if already applied
    if (appliedCharges.some(ac => ac.id === charge.id)) return;
    const newAppliedCharge: AppliedCharge = {
      ...charge,
      appliedId: Math.random().toString(36).substr(2, 9),
      serviceName: service.name,
      serviceId: service.id
    };
    setAppliedCharges([...appliedCharges, newAppliedCharge]);
  };
  const handleRemoveAppliedCharge = (appliedId: string) => {
    setAppliedCharges(appliedCharges.filter(ac => ac.appliedId !== appliedId));
  };
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!gatepassData) {
      newErrors.general = 'Please search and select a gatepass first';
    }
    if (appliedCharges.length === 0) {
      newErrors.general = 'Please apply at least one charge';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSave = () => {
    if (validateForm()) {
      setSaveStatus('success');
      setTimeout(() => {
        setSaveStatus('idle');
        handleReset();
      }, 3000);
    }
  };
  const handleReset = () => {
    setSearchQuery('');
    setGatepassData(null);
    setExpandedServiceId(null);
    setAppliedCharges([]);
    setErrors({});
    setSaveStatus('idle');
  };
  const totalAmount = appliedCharges.reduce((sum, charge) => sum + charge.amount, 0);
  return <div className="flex flex-col gap-6 w-full h-full pb-24 max-w-5xl mx-auto" data-id="element-58">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-59">
        <div data-id="element-60">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-61">Apply Service</h1>
          <p className="text-gray-500 text-sm mt-1" data-id="element-62">
            Apply registered service charges to a gatepass.
          </p>
        </div>

        {saveStatus === 'success' && <div className="flex items-center gap-2 bg-green-50 text-green-700 px-4 py-2 rounded-full border border-green-200 animate-in fade-in slide-in-from-top-4 duration-300" data-id="element-63">
            <CheckCircle2 size={18} data-id="element-64" />
            <span className="font-medium text-sm" data-id="element-65">
              Charges applied successfully!
            </span>
          </div>}
      </div>

      {errors.general && <div className="flex items-center gap-2 text-red-600 bg-red-50 border border-red-200 p-3 rounded-lg text-sm font-medium" data-id="element-66">
          <AlertCircle size={18} data-id="element-67" />
          {errors.general}
        </div>}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start" data-id="element-68">
        {/* LEFT COLUMN: Search & Gatepass Details */}
        <div className="lg:col-span-5 flex flex-col gap-6" data-id="element-69">
          {/* Search Card */}
          <div className={`bg-white p-6 rounded-xl shadow-sm border transition-all duration-300 ${gatepassData ? 'border-orange-200 ring-1 ring-orange-100' : 'border-gray-200'}`} data-id="element-70">
            <div className="flex items-center gap-3 mb-4" data-id="element-71">
              <div className="bg-orange-100 p-2 rounded-lg text-orange-600" data-id="element-72">
                <Search size={20} data-id="element-73" />
              </div>
              <h2 className="text-lg font-bold text-gray-800" data-id="element-74">Find Gatepass</h2>
            </div>

            <div className="flex flex-col gap-2" data-id="element-75">
              <div className="flex gap-2" data-id="element-76">
                <div className="relative flex-1" data-id="element-77">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-78" />
                  <input type="text" value={searchQuery} onChange={e => {
                  setSearchQuery(e.target.value);
                  if (errors.search) setErrors(prev => ({
                    ...prev,
                    search: ''
                  }));
                }} onKeyDown={e => e.key === 'Enter' && handleSearch()} placeholder="Enter Gatepass No." className={`w-full pl-10 pr-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none transition-all ${errors.search ? 'border-red-300 bg-red-50' : 'border-gray-300'}`} data-id="element-79" />
                </div>
                <button onClick={handleSearch} disabled={isSearching} className="bg-gray-900 hover:bg-gray-800 text-white px-5 py-2.5 rounded-lg font-medium transition-colors disabled:opacity-70 flex items-center justify-center min-w-[80px]" data-id="element-80">
                  {isSearching ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-81" /> : 'Find'}
                </button>
              </div>
              {errors.search && <span className="text-xs text-red-500 font-medium ml-1" data-id="element-82">
                  {errors.search}
                </span>}
            </div>
          </div>

          {/* Gatepass Details Card */}
          {gatepassData && <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-83">
              <div className="flex justify-between items-start mb-6 pb-4 border-b border-gray-100" data-id="element-84">
                <div data-id="element-85">
                  <span className="text-xs font-bold text-gray-400 uppercase tracking-wider" data-id="element-86">
                    Gatepass Number
                  </span>
                  <p className="text-lg font-mono font-bold text-gray-900 mt-1" data-id="element-87">
                    {gatepassData.gatepassNumber}
                  </p>
                </div>
                <span className="bg-green-100 text-green-700 text-xs font-bold px-2.5 py-1 rounded-full border border-green-200" data-id="element-88">
                  {gatepassData.status}
                </span>
              </div>

              <div className="space-y-4" data-id="element-89">
                <div className="p-3 bg-gray-50 rounded-lg border border-gray-100 flex items-center gap-3" data-id="element-90">
                  <div className="bg-white p-2 rounded shadow-sm border border-gray-100" data-id="element-91">
                    <Truck size={16} className="text-gray-600" data-id="element-92" />
                  </div>
                  <div data-id="element-93">
                    <span className="text-[10px] font-bold text-gray-400 uppercase block" data-id="element-94">
                      Vehicle No
                    </span>
                    <p className="font-bold text-gray-800 text-sm" data-id="element-95">
                      {gatepassData.vehicleNo}
                    </p>
                  </div>
                </div>

                <div className="p-3 bg-gray-50 rounded-lg border border-gray-100 flex items-center gap-3" data-id="element-96">
                  <div className="bg-white p-2 rounded shadow-sm border border-gray-100" data-id="element-97">
                    <Package size={16} className="text-gray-600" data-id="element-98" />
                  </div>
                  <div data-id="element-99">
                    <span className="text-[10px] font-bold text-gray-400 uppercase block" data-id="element-100">
                      Commodity
                    </span>
                    <p className="font-bold text-gray-800 text-sm" data-id="element-101">
                      {gatepassData.commodity}
                    </p>
                  </div>
                </div>

                <div className="p-3 bg-gray-50 rounded-lg border border-gray-100 flex items-center gap-3" data-id="element-102">
                  <div className="bg-white p-2 rounded shadow-sm border border-gray-100" data-id="element-103">
                    <FileText size={16} className="text-gray-600" data-id="element-104" />
                  </div>
                  <div data-id="element-105">
                    <span className="text-[10px] font-bold text-gray-400 uppercase block" data-id="element-106">
                      Party Name
                    </span>
                    <p className="font-bold text-gray-800 text-sm" data-id="element-107">
                      {gatepassData.partyName}
                    </p>
                  </div>
                </div>
              </div>
            </div>}
        </div>

        {/* RIGHT COLUMN: Services & Applied Charges */}
        <div className={`lg:col-span-7 flex flex-col gap-6 transition-opacity duration-500 ${gatepassData ? 'opacity-100' : 'opacity-50 pointer-events-none'}`} data-id="element-108">
          {/* Available Services List */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200" data-id="element-109">
            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-100" data-id="element-110">
              <div className="bg-blue-100 p-2 rounded-lg text-blue-600" data-id="element-111">
                <Briefcase size={20} data-id="element-112" />
              </div>
              <div data-id="element-113">
                <h2 className="text-lg font-bold text-gray-800" data-id="element-114">
                  Available Services
                </h2>
                <p className="text-xs text-gray-500 mt-0.5" data-id="element-115">
                  Select a service to apply charges
                </p>
              </div>
            </div>

            <div className="space-y-3" data-id="element-116">
              {REGISTERED_SERVICES.map(service => {
              const isExpanded = expandedServiceId === service.id;
              return <div key={service.id} className={`border rounded-lg overflow-hidden transition-all ${isExpanded ? 'border-blue-300 ring-1 ring-blue-100' : 'border-gray-200'}`} data-id="element-117">
                    {/* Service Header Row */}
                    <div onClick={() => toggleServiceExpand(service.id)} className="flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors" data-id="element-118">
                      <div data-id="element-119">
                        <h3 className="font-bold text-gray-800" data-id="element-120">
                          {service.name}
                        </h3>
                        <span className="text-xs font-medium text-gray-500 bg-white px-2 py-0.5 rounded border border-gray-200 mt-1 inline-block" data-id="element-121">
                          {service.category}
                        </span>
                      </div>
                      <div className="text-gray-400" data-id="element-122">
                        {isExpanded ? <ChevronDown size={20} data-id="element-123" /> : <ChevronRight size={20} data-id="element-124" />}
                      </div>
                    </div>

                    {/* Expanded Charges Table */}
                    {isExpanded && <div className="p-4 bg-white border-t border-gray-200 animate-in slide-in-from-top-2 duration-200" data-id="element-125">
                        <table className="w-full text-sm text-left" data-id="element-126">
                          <thead className="text-xs text-gray-500 uppercase font-bold border-b border-gray-100" data-id="element-127">
                            <tr data-id="element-128">
                              <th className="pb-2 font-bold" data-id="element-129">Rate Name</th>
                              <th className="pb-2 font-bold" data-id="element-130">Type</th>
                              <th className="pb-2 font-bold text-right" data-id="element-131">
                                Amount
                              </th>
                              <th className="pb-2 font-bold text-center w-20" data-id="element-132">
                                Action
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100" data-id="element-133">
                            {service.charges.map(charge => {
                        const isApplied = appliedCharges.some(ac => ac.id === charge.id);
                        return <tr key={charge.id} className="hover:bg-gray-50/50" data-id="element-134">
                                  <td className="py-3 text-gray-800 font-medium" data-id="element-135">
                                    {charge.rateName}
                                    <span className="block text-[10px] text-gray-400" data-id="element-136">
                                      FY: {charge.fiscalYear}
                                    </span>
                                  </td>
                                  <td className="py-3 text-gray-600" data-id="element-137">
                                    <span className="bg-gray-100 px-2 py-1 rounded text-[10px] font-medium border border-gray-200" data-id="element-138">
                                      {charge.rateType}
                                    </span>
                                  </td>
                                  <td className="py-3 text-right font-bold text-gray-900" data-id="element-139">
                                    {charge.amount.toLocaleString(undefined, {
                              minimumFractionDigits: 2
                            })}
                                  </td>
                                  <td className="py-3 text-center" data-id="element-140">
                                    <button onClick={() => handleApplyCharge(service, charge)} disabled={isApplied} className={`flex items-center justify-center gap-1 w-full py-1.5 rounded text-xs font-bold transition-colors ${isApplied ? 'bg-green-50 text-green-600 border border-green-200 cursor-default' : 'bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100'}`} data-id="element-141">
                                      {isApplied ? <>
                                          <CheckCircle2 size={14} data-id="element-142" /> Applied
                                        </> : <>
                                          <Plus size={14} data-id="element-143" /> Apply
                                        </>}
                                    </button>
                                  </td>
                                </tr>;
                      })}
                          </tbody>
                        </table>
                      </div>}
                  </div>;
            })}
            </div>
          </div>

          {/* Applied Charges Summary */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200" data-id="element-144">
            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-100" data-id="element-145">
              <div className="bg-orange-100 p-2 rounded-lg text-orange-600" data-id="element-146">
                <DollarSign size={20} data-id="element-147" />
              </div>
              <div data-id="element-148">
                <h2 className="text-lg font-bold text-gray-800" data-id="element-149">
                  Applied Charges
                </h2>
                <p className="text-xs text-gray-500 mt-0.5" data-id="element-150">
                  Review charges before saving
                </p>
              </div>
            </div>

            {appliedCharges.length > 0 ? <div className="space-y-4" data-id="element-151">
                <div className="overflow-hidden border border-gray-200 rounded-lg" data-id="element-152">
                  <table className="w-full text-sm text-left" data-id="element-153">
                    <thead className="bg-gray-50 text-gray-700 font-bold uppercase text-xs border-b border-gray-200" data-id="element-154">
                      <tr data-id="element-155">
                        <th className="px-4 py-3" data-id="element-156">Service & Rate</th>
                        <th className="px-4 py-3 text-right" data-id="element-157">Amount (NPR)</th>
                        <th className="px-4 py-3 w-16 text-center" data-id="element-158"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200" data-id="element-159">
                      {appliedCharges.map(charge => <tr key={charge.appliedId} className="hover:bg-gray-50 transition-colors" data-id="element-160">
                          <td className="px-4 py-3" data-id="element-161">
                            <p className="font-bold text-gray-900" data-id="element-162">
                              {charge.serviceName}
                            </p>
                            <p className="text-xs text-gray-500" data-id="element-163">
                              {charge.rateName} ({charge.rateType})
                            </p>
                          </td>
                          <td className="px-4 py-3 text-right font-bold text-gray-900" data-id="element-164">
                            {charge.amount.toLocaleString(undefined, {
                        minimumFractionDigits: 2
                      })}
                          </td>
                          <td className="px-4 py-3 text-center" data-id="element-165">
                            <button onClick={() => handleRemoveAppliedCharge(charge.appliedId)} className="text-gray-400 hover:text-red-500 transition-colors p-1" title="Remove Charge" data-id="element-166">
                              <Trash2 size={16} data-id="element-167" />
                            </button>
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-gray-100" data-id="element-168">
                  <span className="text-sm font-bold text-gray-700 uppercase tracking-wider" data-id="element-169">
                    Total Amount
                  </span>
                  <span className="text-2xl font-bold text-orange-600 font-mono" data-id="element-170">
                    NPR{' '}
                    {totalAmount.toLocaleString(undefined, {
                  minimumFractionDigits: 2
                })}
                  </span>
                </div>
              </div> : <div className="flex flex-col items-center justify-center py-10 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200 text-gray-400" data-id="element-171">
                <DollarSign size={32} className="mb-2 opacity-50" data-id="element-172" />
                <p className="text-sm font-medium" data-id="element-173">No charges applied yet.</p>
                <p className="text-xs mt-1" data-id="element-174">
                  Select a service above to apply charges.
                </p>
              </div>}
          </div>
        </div>
      </div>

      {/* Footer Action Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] z-10 lg:pl-72" data-id="element-175">
        <div className="max-w-5xl mx-auto flex justify-between items-center" data-id="element-176">
          <button onClick={handleReset} className="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-177">
            <RotateCcw size={18} data-id="element-178" />
            Reset Form
          </button>

          <button onClick={handleSave} disabled={!gatepassData || appliedCharges.length === 0} className={`flex items-center gap-2 px-8 py-2.5 rounded-lg font-bold text-white shadow-md transition-all ${gatepassData && appliedCharges.length > 0 ? 'bg-orange-500 hover:bg-orange-600 hover:shadow-lg hover:-translate-y-0.5' : 'bg-gray-300 cursor-not-allowed'}`} data-id="element-179">
            <Save size={18} data-id="element-180" />
            Save Applied Charges
          </button>
        </div>
      </div>
    </div>;
}